import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

const CaseStudiesSection = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [activeCase, setActiveCase] = useState(0);

  const caseStudies = [
    {
      title: language === 'fr' ? 'Optimisation du système de mesure de débit pour une raffinerie' : 
             language === 'ar' ? 'تحسين نظام قياس التدفق لمصفاة نفط' : 
             'Flow measurement system optimization for an oil refinery',
      industry: language === 'fr' ? 'Pétrole et gaz' : 
                language === 'ar' ? 'النفط والغاز' : 
                'Oil & Gas',
      location: language === 'fr' ? 'Skikda, Algérie' : 
                language === 'ar' ? 'سكيكدة، الجزائر' : 
                'Skikda, Algeria',
      challenge: language === 'fr' 
        ? 'Une importante raffinerie de pétrole faisait face à des problèmes de précision dans la mesure des débits de produits, entraînant des inefficacités dans le processus et des pertes potentielles de revenus.' 
        : language === 'ar' 
        ? 'كانت مصفاة نفط كبيرة تواجه مشاكل في دقة قياس تدفق المنتجات، مما أدى إلى عدم كفاءة في العملية وخسائر محتملة في الإيرادات.' 
        : 'A major oil refinery was facing accuracy issues in product flow measurement, resulting in process inefficiencies and potential revenue losses.',
      solution: language === 'fr' 
        ? 'Installation de débitmètres ultrasoniques Panametrics de haute précision avec étalonnage sur site et formation du personnel pour une maintenance optimale.' 
        : language === 'ar' 
        ? 'تركيب عدادات تدفق بالموجات فوق الصوتية عالية الدقة من Panametrics مع معايرة في الموقع وتدريب الموظفين للصيانة المثلى.' 
        : 'Installation of high-precision Panametrics ultrasonic flowmeters with on-site calibration and staff training for optimal maintenance.',
      results: language === 'fr' 
        ? 'Amélioration de la précision de mesure de 97%, réduction des temps d\'arrêt de 35%, et augmentation de l\'efficacité opérationnelle globale de 15%.' 
        : language === 'ar' 
        ? 'تحسين دقة القياس بنسبة 97%، وتقليل وقت التوقف بنسبة 35%، وزيادة الكفاءة التشغيلية العامة بنسبة 15%.' 
        : 'Improved measurement accuracy by 97%, reduced downtime by 35%, and increased overall operational efficiency by 15%.',
      testimonial: language === 'fr' 
        ? '"L\'expertise technique d\'ASSCO et leur connaissance approfondie des produits Panametrics ont été déterminantes pour résoudre nos problèmes de mesure de débit."' 
        : language === 'ar' 
        ? '"كانت الخبرة الفنية لشركة ASSCO ومعرفتهم العميقة بمنتجات Panametrics حاسمة في حل مشاكل قياس التدفق لدينا."' 
        : '"ASSCO\'s technical expertise and deep knowledge of Panametrics products were instrumental in solving our flow measurement issues."',
      testimonialAuthor: language === 'fr' ? 'Directeur des opérations, Raffinerie de Skikda' : 
                          language === 'ar' ? 'مدير العمليات، مصفاة سكيكدة' : 
                          'Operations Director, Skikda Refinery',
      image: '/images/case-studies/oil-refinery-skikda.jpg'
    },
    {
      title: language === 'fr' ? 'Modernisation du système d\'analyse de gaz pour une usine chimique' : 
             language === 'ar' ? 'تحديث نظام تحليل الغاز لمصنع كيميائي' : 
             'Gas analysis system upgrade for a chemical plant',
      industry: language === 'fr' ? 'Chimie' : 
                language === 'ar' ? 'الكيمياء' : 
                'Chemical',
      location: language === 'fr' ? 'Arzew, Algérie' : 
                language === 'ar' ? 'أرزيو، الجزائر' : 
                'Arzew, Algeria',
      challenge: language === 'fr' 
        ? 'Une usine chimique utilisait un système d\'analyse de gaz obsolète qui ne fournissait pas les données en temps réel nécessaires pour optimiser les processus de production.' 
        : language === 'ar' 
        ? 'كان مصنع كيميائي يستخدم نظامًا قديمًا لتحليل الغاز لم يوفر البيانات في الوقت الفعلي اللازمة لتحسين عمليات الإنتاج.' 
        : 'A chemical plant was using an outdated gas analysis system that didn\'t provide the real-time data needed to optimize production processes.',
      solution: language === 'fr' 
        ? 'Implémentation d\'analyseurs de gaz Panametrics XMO2 avec intégration au système de contrôle existant et formation complète du personnel.' 
        : language === 'ar' 
        ? 'تنفيذ محللات الغاز Panametrics XMO2 مع التكامل مع نظام التحكم الحالي والتدريب الشامل للموظفين.' 
        : 'Implementation of Panametrics XMO2 gas analyzers with integration to the existing control system and comprehensive staff training.',
      results: language === 'fr' 
        ? 'Amélioration de la qualité des produits de 25%, réduction des déchets de 30%, et augmentation de la productivité de 20% grâce à l\'optimisation des processus basée sur des données en temps réel.' 
        : language === 'ar' 
        ? 'تحسين جودة المنتج بنسبة 25%، وتقليل النفايات بنسبة 30%، وزيادة الإنتاجية بنسبة 20% من خلال تحسين العملية بناءً على البيانات في الوقت الفعلي.' 
        : 'Improved product quality by 25%, reduced waste by 30%, and increased productivity by 20% through real-time data-based process optimization.',
      testimonial: language === 'fr' 
        ? '"L\'équipe d\'ASSCO a fourni une solution complète qui a transformé notre capacité à surveiller et à contrôler nos processus de production."' 
        : language === 'ar' 
        ? '"قدم فريق ASSCO حلاً شاملاً غير قدرتنا على مراقبة عمليات الإنتاج لدينا والتحكم فيها."' 
        : '"The ASSCO team provided a comprehensive solution that transformed our ability to monitor and control our production processes."',
      testimonialAuthor: language === 'fr' ? 'Ingénieur en chef, Usine chimique d\'Arzew' : 
                          language === 'ar' ? 'المهندس الرئيسي، مصنع أرزيو الكيميائي' : 
                          'Chief Engineer, Arzew Chemical Plant',
      image: '/images/case-studies/chemical-plant-arzew.jpg'
    },
    {
      title: language === 'fr' ? 'Installation de systèmes de surveillance d\'humidité pour une centrale électrique' : 
             language === 'ar' ? 'تركيب أنظمة مراقبة الرطوبة لمحطة توليد الكهرباء' : 
             'Moisture monitoring system installation for a power plant',
      industry: language === 'fr' ? 'Production d\'énergie' : 
                language === 'ar' ? 'توليد الطاقة' : 
                'Power Generation',
      location: language === 'fr' ? 'Hassi Messaoud, Algérie' : 
                language === 'ar' ? 'حاسي مسعود، الجزائر' : 
                'Hassi Messaoud, Algeria',
      challenge: language === 'fr' 
        ? 'Une centrale électrique avait besoin d\'un système fiable pour surveiller les niveaux d\'humidité dans les turbines à gaz afin de prévenir les dommages aux équipements et d\'optimiser l\'efficacité.' 
        : language === 'ar' 
        ? 'كانت محطة توليد الكهرباء بحاجة إلى نظام موثوق لمراقبة مستويات الرطوبة في التوربينات الغازية لمنع تلف المعدات وتحسين الكفاءة.' 
        : 'A power plant needed a reliable system to monitor moisture levels in gas turbines to prevent equipment damage and optimize efficiency.',
      solution: language === 'fr' 
        ? 'Déploiement d\'analyseurs d\'humidité Panametrics avec surveillance en temps réel et intégration au système de contrôle de la centrale.' 
        : language === 'ar' 
        ? 'نشر محللات الرطوبة من Panametrics مع المراقبة في الوقت الفعلي والتكامل مع نظام التحكم في المحطة.' 
        : 'Deployment of Panametrics moisture analyzers with real-time monitoring and integration with the plant\'s control system.',
      results: language === 'fr' 
        ? 'Réduction des arrêts non planifiés de 40%, augmentation de la durée de vie des équipements de 30%, et économies annuelles estimées à plus de 500 000 euros.' 
        : language === 'ar' 
        ? 'تقليل التوقف غير المخطط له بنسبة 40%، وزيادة عمر المعدات بنسبة 30%، ووفورات سنوية تقدر بأكثر من 500000 يورو.' 
        : 'Reduced unplanned downtime by 40%, increased equipment lifespan by 30%, and estimated annual savings of over €500,000.',
      testimonial: language === 'fr' 
        ? '"La solution d\'ASSCO nous a permis d\'identifier et de résoudre les problèmes d\'humidité avant qu\'ils ne causent des dommages coûteux à nos turbines."' 
        : language === 'ar' 
        ? '"سمح لنا حل ASSCO بتحديد مشاكل الرطوبة وحلها قبل أن تتسبب في أضرار مكلفة لتوربيناتنا."' 
        : '"ASSCO\'s solution allowed us to identify and address moisture issues before they caused costly damage to our turbines."',
      testimonialAuthor: language === 'fr' ? 'Directeur technique, Centrale électrique de Hassi Messaoud' : 
                          language === 'ar' ? 'المدير الفني، محطة كهرباء حاسي مسعود' : 
                          'Technical Director, Hassi Messaoud Power Plant',
      image: '/images/case-studies/power-plant-hassi-messaoud.jpg'
    }
  ];

  return (
    <section className="section section-light" style={{ 
      position: 'relative', 
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      padding: '6rem 0'
    }}>
      {/* Enhanced background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.06) 0%, transparent 70%)',
        animation: 'float 20s ease-in-out infinite',
        zIndex: 1
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '15%',
        right: '8%',
        width: '200px',
        height: '200px',
        borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
        background: 'rgba(255, 107, 0, 0.03)',
        animation: 'float 15s ease-in-out infinite reverse',
        zIndex: 1
      }}></div>
      
      {/* Grid pattern overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FF6B00' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        opacity: 0.4,
        zIndex: 1
      }}></div>
      
      <div className="container" style={{ position: 'relative', zIndex: 2, maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
        {/* Enhanced header section */}
        <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
          <span className="fade-in" style={{
            display: 'inline-block',
            padding: '0.75rem 2rem',
            background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 140, 63, 0.1) 100%)',
            color: 'var(--assco-orange)',
            fontWeight: '700',
            fontSize: '0.9rem',
            borderRadius: '50px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 107, 0, 0.2)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <span style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              animation: 'shimmer 3s infinite'
            }}></span>
            {language === 'fr' ? 'PROJETS RÉUSSIS' : 
             language === 'ar' ? 'مشاريع ناجحة' : 
             'SUCCESS STORIES'}
          </span>
          
          <h2 className="text-center fade-in" style={{ 
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', 
            marginBottom: '1.5rem',
            position: 'relative',
            paddingBottom: '1.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {language === 'fr' ? 'Études de cas' : 
             language === 'ar' ? 'دراسات الحالة' : 
             'Case Studies'}
            <span style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '5px',
              background: 'linear-gradient(90deg, var(--assco-orange), #ff8c3f)',
              borderRadius: '3px',
              boxShadow: '0 4px 15px rgba(255, 107, 0, 0.3)'
            }}></span>
          </h2>
          
          <p className="subtitle text-center fade-in" style={{ 
            maxWidth: '900px',
            margin: '0 auto',
            fontSize: '1.3rem',
            color: '#64748b',
            lineHeight: '1.8',
            fontWeight: '400'
          }}>
            {language === 'fr' 
              ? 'Découvrez comment nous avons relevé des défis techniques complexes et fourni des solutions d\'instrumentation sur mesure pour nos clients dans le secteur Pétrole, Gaz et Industrie.' 
              : language === 'ar'
              ? 'اكتشف كيف تغلبنا على التحديات الفنية المعقدة وقدمنا حلول قياس مخصصة لعملائنا في قطاع النفط والغاز والصناعة.'
              : 'Discover how we tackled complex technical challenges and delivered tailored instrumentation solutions for our clients in the Oil, Gas and Industry sector.'}
          </p>
        </div>
        
        {/* Enhanced navigation cards */}
        <div className="case-studies-nav fade-in" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '1.5rem',
          marginBottom: '5rem',
          maxWidth: '1200px',
          margin: '0 auto 5rem'
        }}>
          {caseStudies.map((caseStudy, index) => (
            <div
              key={index}
              className={`case-study-nav-card ${activeCase === index ? 'active' : ''}`}
              style={{
                background: activeCase === index 
                  ? 'linear-gradient(135deg, var(--assco-orange) 0%, #ff8c3f 100%)' 
                  : 'white',
                borderRadius: '20px',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                border: activeCase === index ? 'none' : '1px solid #e2e8f0',
                boxShadow: activeCase === index 
                  ? '0 20px 40px -15px rgba(255, 107, 0, 0.4), 0 0 0 1px rgba(255, 107, 0, 0.1)' 
                  : '0 4px 20px rgba(0, 0, 0, 0.08)',
                position: 'relative',
                overflow: 'hidden',
                transform: activeCase === index ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)'
              }}
              onClick={() => setActiveCase(index)}
              onMouseOver={(e) => {
                if (activeCase !== index) {
                  e.currentTarget.style.transform = 'translateY(-5px) scale(1.01)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(255, 107, 0, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                if (activeCase !== index) {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              {/* Animated background pattern for active card */}
              {activeCase === index && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  zIndex: 1
                }}></div>
              )}
              
              <div style={{ position: 'relative', zIndex: 2 }}>
                {/* Header with industry badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '16px',
                      background: activeCase === index 
                        ? 'rgba(255, 255, 255, 0.2)' 
                        : 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 140, 63, 0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: activeCase === index ? 'white' : 'var(--assco-orange)',
                      flexShrink: 0
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    
                    <span style={{
                      background: activeCase === index 
                        ? 'rgba(255, 255, 255, 0.2)' 
                        : 'rgba(255, 107, 0, 0.1)',
                      color: activeCase === index ? 'white' : 'var(--assco-orange)',
                      padding: '0.5rem 1rem',
                      borderRadius: '25px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {caseStudy.industry}
                    </span>
                  </div>
                  
                  {activeCase === index && (
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      animation: 'pulse 2s infinite'
                    }}></div>
                  )}
                </div>
                
                {/* Title and location */}
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: activeCase === index ? 'white' : '#1e293b',
                  marginBottom: '0.75rem',
                  lineHeight: '1.4'
                }}>
                  {caseStudy.title}
                </h3>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: activeCase === index ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                  fontSize: '0.95rem',
                  marginBottom: '1rem'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '16px', height: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {caseStudy.location}
                </div>
                
                {/* Progress indicator */}
                <div style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: activeCase === index ? 'rgba(255, 255, 255, 0.2)' : '#e2e8f0',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: activeCase === index ? '100%' : '0%',
                    height: '100%',
                    backgroundColor: activeCase === index ? 'white' : 'var(--assco-orange)',
                    borderRadius: '2px',
                    transition: 'width 0.6s ease'
                  }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="case-study-content fade-in" style={{ 
          animationDelay: '0.3s',
          backgroundColor: 'var(--assco-gray-50)',
          borderRadius: 'var(--radius-xl)',
          padding: '3rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)'
        }}>
          <div className="grid" style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)', 
            gap: '3.5rem',
            alignItems: 'center'
          }}>
            <div className="col-span-6 md-col-span-12">
              <div style={{ position: 'relative', padding: '1rem' }}>
                {/* Background decorative shape */}
                <div style={{
                  position: 'absolute',
                  top: '2rem',
                  left: '-1rem',
                  width: '70%',
                  height: '70%',
                  backgroundColor: 'rgba(255, 107, 0, 0.08)',
                  borderRadius: '2rem',
                  transform: 'rotate(-3deg)',
                  zIndex: 1
                }}></div>
                
                <div className="case-study-image" style={{ 
                  position: 'relative',
                  borderRadius: 'var(--radius-xl)',
                  overflow: 'hidden',
                  boxShadow: '0 20px 30px rgba(0, 0, 0, 0.1)',
                  height: '420px',
                  backgroundColor: '#f1f5f9',
                  backgroundImage: `url(${caseStudies[activeCase].image}), linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)`,
                  backgroundSize: 'cover, cover',
                  backgroundPosition: 'center, center',
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Placeholder for real image */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: '#64748b',
                    zIndex: 1
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 107, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                      border: '2px dashed rgba(255, 107, 0, 0.3)'
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px', color: 'var(--assco-orange)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p style={{ 
                      fontSize: '0.875rem', 
                      margin: 0, 
                      fontWeight: '500',
                      opacity: 0.7
                    }}>
                      {language === 'fr' ? 'Image réelle à ajouter' :
                       language === 'ar' ? 'صورة حقيقية لإضافتها' :
                       'Real image to be added'}
                    </p>
                  </div>
                  
                  {/* Project stats banner */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    padding: '1.5rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    backdropFilter: 'blur(5px)',
                    display: 'flex',
                    justifyContent: 'space-around'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--assco-orange)' }}>97%</div>
                      <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                        {language === 'fr' ? 'Précision' : 
                         language === 'ar' ? 'دقة' : 
                         'Accuracy'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--assco-orange)' }}>35%</div>
                      <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                        {language === 'fr' ? 'Temps économisé' : 
                         language === 'ar' ? 'وقت موفر' : 
                         'Time saved'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--assco-orange)' }}>15%</div>
                      <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                        {language === 'fr' ? 'Efficacité' : 
                         language === 'ar' ? 'كفاءة' : 
                         'Efficiency'}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    backgroundColor: 'var(--assco-orange)',
                    color: 'white',
                    padding: '0.75rem 1.25rem',
                    borderRadius: 'var(--radius-xl)',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    boxShadow: '0 10px 15px -3px rgba(255, 107, 0, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {caseStudies[activeCase].industry}
                  </div>
                </div>
                
                {/* Location tag */}
                <div style={{
                  position: 'absolute',
                  bottom: '2rem',
                  right: '-1rem',
                  backgroundColor: 'white',
                  borderRadius: 'var(--radius-lg)',
                  padding: '0.75rem 1.5rem',
                  boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  zIndex: 3
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 107, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--assco-orange)'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span style={{ fontWeight: '500' }}>
                    {caseStudies[activeCase].location}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="col-span-6 md-col-span-12">
              <div style={{ backgroundColor: 'white', borderRadius: 'var(--radius-xl)', padding: '2.5rem', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)' }}>
                <h3 style={{ 
                  fontSize: '2.25rem', 
                  marginBottom: '1.5rem',
                  color: 'var(--assco-gray-900)',
                  fontWeight: '700',
                  position: 'relative',
                  paddingBottom: '1.25rem'
                }}>
                  {caseStudies[activeCase].title}
                  <span style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '60px',
                    height: '4px',
                    backgroundColor: 'var(--assco-orange)',
                    borderRadius: '2px'
                  }}></span>
                </h3>
                
                <div style={{ marginBottom: '2.5rem' }}>
                  {/* Horizontal container for challenge and solution */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: '2rem',
                    marginBottom: '2rem',
                    alignItems: 'stretch'
                  }}>
                  <div className="challenge-section" style={{ 
                    backgroundColor: 'var(--assco-gray-50)', 
                    borderRadius: 'var(--radius-xl)',
                    padding: '1.75rem',
                    position: 'relative',
                      overflow: 'hidden',
                      flex: 1,
                      minWidth: '250px'
                  }}>
                    {/* Decorative pattern removed for compatibility */}
                    
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 107, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--assco-orange)',
                        flexShrink: 0
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 style={{ 
                          fontSize: '1.25rem',
                          marginBottom: '0.75rem',
                          color: 'var(--assco-gray-800)',
                          fontWeight: '600'
                        }}>
                          {language === 'fr' ? 'Le défi' : 
                           language === 'ar' ? 'التحدي' : 
                           'The challenge'}
                        </h4>
                        
                        <p style={{ 
                          fontSize: '1.1rem', 
                          lineHeight: '1.8',
                          color: 'var(--assco-gray-700)'
                        }}>
                          {caseStudies[activeCase].challenge}
                        </p>
                    </div>
                  </div>
                  
                  <div className="solution-section" style={{
                    padding: '1.75rem',
                    border: '2px dashed rgba(255, 107, 0, 0.2)',
                    borderRadius: 'var(--radius-xl)',
                      flex: 1,
                      minWidth: '250px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 107, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--assco-orange)',
                        flexShrink: 0
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <h4 style={{ 
                          fontSize: '1.25rem',
                          marginBottom: '0.75rem',
                          color: 'var(--assco-gray-800)',
                          fontWeight: '600'
                        }}>
                          {language === 'fr' ? 'Notre solution' : 
                           language === 'ar' ? 'حلنا' : 
                           'Our solution'}
                        </h4>
                        
                        <p style={{ 
                          fontSize: '1.1rem', 
                          lineHeight: '1.8',
                          color: 'var(--assco-gray-700)'
                        }}>
                          {caseStudies[activeCase].solution}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="results-section" style={{
                    backgroundColor: 'rgba(255, 107, 0, 0.05)',
                    borderRadius: 'var(--radius-xl)',
                      padding: '1.75rem',
                      flex: 1,
                      minWidth: '250px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: 'var(--assco-orange)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        flexShrink: 0,
                        boxShadow: '0 10px 15px -3px rgba(255, 107, 0, 0.2)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 style={{ 
                          fontSize: '1.25rem',
                          marginBottom: '0.75rem',
                          color: 'var(--assco-gray-800)',
                          fontWeight: '600'
                        }}>
                          {language === 'fr' ? 'Les résultats' : 
                           language === 'ar' ? 'النتائج' : 
                           'The results'}
                        </h4>
                        
                        <p style={{ 
                          fontSize: '1.1rem', 
                          lineHeight: '1.8',
                          color: 'var(--assco-gray-700)'
                        }}>
                          {caseStudies[activeCase].results}
                        </p>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="testimonial" style={{
                  background: 'linear-gradient(to right, var(--assco-orange), #ff8c3f)',
                  borderRadius: 'var(--radius-xl)',
                  padding: '2rem',
                  position: 'relative',
                  color: 'white',
                  boxShadow: '0 15px 30px rgba(255, 107, 0, 0.15)',
                  overflow: 'hidden'
                }}>
                  {/* Decorative patterns removed for compatibility */}
                
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px', color: 'white' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                
                  <p style={{ 
                    fontSize: '1.25rem', 
                    fontStyle: 'italic',
                    fontWeight: '500',
                    lineHeight: '1.8',
                    color: 'white',
                    marginBottom: '1.5rem',
                    position: 'relative',
                    zIndex: 2
                  }}>
                    {caseStudies[activeCase].testimonial}
                  </p>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                    paddingTop: '1.5rem',
                    marginTop: '1.5rem'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: 'white'
                    }}>
                      {caseStudies[activeCase].testimonialAuthor.charAt(0)}
                    </div>
                    <div>
                      <p style={{ 
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: 'white',
                        margin: 0
                      }}>
                        {caseStudies[activeCase].testimonialAuthor}
                      </p>
                      <p style={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        margin: 0
                      }}>
                        {language === 'fr' ? 'Client satisfait' : 
                         language === 'ar' ? 'عميل راضٍ' : 
                         'Satisfied Client'}
                      </p>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
        
        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease forwards;
        }
        
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Enhanced responsive design */
        @media (max-width: 768px) {
          .case-studies-nav {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          
          .case-study-nav-card {
            padding: 1.5rem !important;
          }
          
          .grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          
          .col-span-6 {
            grid-column: span 12 / span 12 !important;
          }
          
          .case-study-content {
            padding: 2rem !important;
          }
        }
        
        @media (max-width: 480px) {
          .case-study-nav-card {
            padding: 1rem !important;
          }
          
          .case-study-content {
            padding: 1.5rem !important;
          }
        }
        
        /* Improved hover animations */
        .case-study-nav-card:hover {
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        /* Enhanced visual elements */
        .case-study-image {
          transition: transform 0.3s ease;
        }
        
        .case-study-image:hover {
          transform: scale(1.02);
        }
      `}</style>
    </section>
  );
};

export default CaseStudiesSection;
